// src/components/leitner/SteppedCalculationInput.jsx
// Step-by-step calculation input for both creators and learners

import React, { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Brain } from 'lucide-react';
import InlineMathField from '../math/InlineMathField';
import { MathRenderer } from '../math/MathRenderer';

// ============== CREATOR INPUT - Define expected steps ==============
export const SteppedCalculationCreator = ({
  steps = [],
  setSteps,
  finalAnswer = '',
  setFinalAnswer,
  compact = false
}) => {
  const [expandedStepReasoning, setExpandedStepReasoning] = useState(() => {
    const initial = {};
    steps.forEach((step) => {
      initial[step.id] = !!step.reasoning?.trim();
    });
    return initial;
  });

  const addStep = () => {
    if (steps.length >= 15) return;
    setSteps([...steps, { id: `step_${Date.now()}`, text: '', hint: '', reasoning: '' }]);
  };

  const updateStep = (index, field, value) => {
    const updated = [...steps];
    updated[index] = { ...updated[index], [field]: value };
    setSteps(updated);
  };

  const removeStep = (index) => {
    if (steps.length <= 1) return;
    setSteps(steps.filter((_, i) => i !== index));
  };

  const moveStep = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === steps.length - 1)) return;
    const newSteps = [...steps];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[swapIndex]] = [newSteps[swapIndex], newSteps[index]];
    setSteps(newSteps);
  };

  const toggleStepReasoning = (stepId) => {
    setExpandedStepReasoning((prev) => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-300">Solution Steps</label>
        <button
          type="button"
          onClick={addStep}
          disabled={steps.length >= 15}
          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 disabled:opacity-50"
        >
          <Plus size={12} /> Add Step
        </button>
      </div>

      <p className="text-xs text-slate-500">Define the expected solution steps. Each step can have an optional hint.</p>

      <div className="space-y-2">
        {steps.map((step, index) => (
          <div key={step.id} className="p-3 bg-slate-700/50 rounded-lg border border-slate-600">
            <div className="flex items-start gap-2">
              {/* Reorder controls */}
              <div className="flex flex-col gap-0.5 pt-2">
                <button
                  type="button"
                  onClick={() => moveStep(index, 'up')}
                  disabled={index === 0}
                  className="p-0.5 text-slate-400 hover:text-slate-200 disabled:opacity-30"
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => moveStep(index, 'down')}
                  disabled={index === steps.length - 1}
                  className="p-0.5 text-slate-400 hover:text-slate-200 disabled:opacity-30"
                >
                  <ChevronDown size={12} />
                </button>
              </div>

              {/* Step number */}
              <div className="w-7 h-7 flex items-center justify-center bg-indigo-500/30 border border-indigo-500/50 rounded-full text-sm text-indigo-300 font-bold shrink-0">
                {index + 1}
              </div>

              {/* Step content */}
              <div className="flex-1 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <InlineMathField
                      value={step.text}
                      onChange={(text) => updateStep(index, 'text', text)}
                      rows={1}
                      placeholder={`Step ${index + 1}: Expected work/equation (Ctrl+M for math)`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleStepReasoning(step.id)}
                    title={(expandedStepReasoning[step.id] || step.reasoning?.trim()) ? 'Hide Reasoning' : 'Add Reasoning'}
                    className={`text-[11px] px-2 py-1 rounded border transition-colors ${(expandedStepReasoning[step.id] || step.reasoning?.trim())
                      ? 'border-indigo-500 bg-indigo-500/20 text-indigo-300'
                      : 'border-slate-600 bg-slate-800 text-slate-400 hover:border-slate-500'
                      }`}
                  >
                    {(expandedStepReasoning[step.id] || step.reasoning?.trim()) ? 'Hide Reasoning' : <Brain size={14} />}
                  </button>
                </div>
                <input
                  type="text"
                  value={step.hint || ''}
                  onChange={(e) => updateStep(index, 'hint', e.target.value)}
                  placeholder="Optional hint for this step..."
                  className="w-full px-3 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-xs placeholder-slate-500"
                />
                {(expandedStepReasoning[step.id] || step.reasoning?.trim()) && (
                  <div className={`${compact ? 'mt-1' : 'mt-2'} space-y-1`}>
                    <label className="block text-xs font-medium text-slate-400">Reasoning (optional)</label>
                    <InlineMathField
                      value={step.reasoning || ''}
                      onChange={(text) => updateStep(index, 'reasoning', text)}
                      rows={2}
                      placeholder="Explain the reasoning (Ctrl+M for math)"
                      className="text-xs"
                    />
                    {step.reasoning?.trim() && (
                      <div className="rounded-md border border-slate-700 bg-slate-900/40 p-2">
                        <div className="text-[10px] text-slate-500 mb-1">Preview</div>
                        <div className="prose prose-xs prose-invert max-w-none text-slate-200">
                          <MathRenderer content={step.reasoning} enableMarkdown={true} enableMath={true} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Delete */}
              {steps.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeStep(index)}
                  className="p-1.5 text-red-400 hover:text-red-300 shrink-0"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Final Answer */}
      <div className="pt-3 border-t border-slate-600">
        <label className="block text-sm font-medium text-slate-300 mb-2">Final Answer</label>
        <InlineMathField
          value={finalAnswer}
          onChange={setFinalAnswer}
          rows={1}
          placeholder="Expected final answer (Ctrl+M for math)"
        />
      </div>

      {steps.length < 1 && (
        <p className="text-sm text-amber-400">Add at least 1 step</p>
      )}
    </div>
  );
};

// ============== LEARNER INPUT - Enter solution steps ==============
export const SteppedCalculationAnswer = ({
  value = [], // Array of { id, text }
  onChange,
  disabled = false,
  minSteps = 1,
  maxSteps = 15
}) => {
  // Initialize with one empty step if empty
  const steps = value.length > 0 ? value : [{ id: `step_${Date.now()}`, text: '' }];

  const addStep = () => {
    if (steps.length >= maxSteps) return;
    const newSteps = [...steps, { id: `step_${Date.now()}`, text: '' }];
    onChange(newSteps);
  };

  const updateStep = (index, text) => {
    const updated = [...steps];
    updated[index] = { ...updated[index], text };
    onChange(updated);
  };

  const removeStep = (index) => {
    if (steps.length <= minSteps) return;
    const updated = steps.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">Show your working step by step:</p>
        <span className="text-xs text-slate-500">{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="space-y-2">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-start gap-2">
            {/* Step number */}
            <div className="w-8 h-8 flex items-center justify-center bg-slate-700 border border-slate-600 rounded-full text-sm text-slate-300 font-medium shrink-0 mt-1">
              {index + 1}
            </div>

            {/* Step input */}
            <div className="flex-1">
              <InlineMathField
                value={step.text}
                onChange={(text) => updateStep(index, text)}
                rows={1}
                placeholder={index === 0 ? "Start your working here... (Ctrl+M for math)" : `Step ${index + 1}... (Ctrl+M for math)`}
                disabled={disabled}
              />
            </div>

            {/* Delete button */}
            {steps.length > minSteps && !disabled && (
              <button
                type="button"
                onClick={() => removeStep(index)}
                className="p-2 text-slate-500 hover:text-red-400 transition shrink-0"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add step button */}
      {!disabled && steps.length < maxSteps && (
        <button
          type="button"
          onClick={addStep}
          className="w-full py-2.5 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-indigo-500 hover:text-indigo-400 transition flex items-center justify-center gap-2 text-sm"
        >
          <Plus size={16} />
          Add Step
        </button>
      )}
    </div>
  );
};

export default {
  SteppedCalculationCreator,
  SteppedCalculationAnswer
};
